import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { gethotelinfoIdAPI, getHotelInsightsAPI } from "../../helpers/api.ts";
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  Row,
  Col,
  Badge,
  Tabs,
  Tab,
  Table,
  Spinner,
  Alert,
  Button,
  Form
} from 'react-bootstrap';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import './HotelDetails.css';
import SpaAppointmentStatusBadge from '../../customcomponents/SpaAppointmentStatusBadge';

const HotelDetails = () => {
  const { hotel_id } = useParams();
  const [hotelData, setHotelData] = useState(null);
  const [insightsData, setInsightsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('rooms');
  const [spaAppointments, setSpaAppointments] = useState([]);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    roomType: '',
    acType: '',
    priceRangeMin: '',
    priceRangeMax: '',
    menuCategory: '',
    limit: 10
  });
  const navigate = useNavigate();

  const fetchInsights = useCallback(async () => {
    try {
      setLoading(true);
      
      // Prepare filter data, removing empty values
      const filterData = {};
      Object.keys(filters).forEach(key => {
        if (filters[key] !== '' && filters[key] !== null && filters[key] !== undefined) {
          filterData[key] = filters[key];
        }
      });

      const insightsResponse = await getHotelInsightsAPI(hotel_id, filterData);
      
      if (insightsResponse.success) {
        console.log('Insights data structure:', {
          hasTopRooms: Array.isArray(insightsResponse.data.topRooms),
          topRoomsCount: insightsResponse.data.topRooms?.length,
          hasTopMenuItems: Array.isArray(insightsResponse.data.topMenuItems),
          topMenuItemsCount: insightsResponse.data.topMenuItems?.length,
          hasTopSpaServices: Array.isArray(insightsResponse.data.topSpaServices),
          topSpaServicesCount: insightsResponse.data.topSpaServices?.length,
        });
        setInsightsData(insightsResponse.data);
        setSpaAppointments(insightsResponse.data.spa_appointments);
      }
    } catch (err) {
      console.error('Error fetching insights:', err);
    } finally {
      setLoading(false);
    }
  }, [hotel_id, filters]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const hotelResponse = await gethotelinfoIdAPI(hotel_id);

        if (hotelResponse.success) {
          setHotelData(hotelResponse.data);
          setError(null);
        } else {
          setError('Failed to fetch hotel details.');
        }
      } catch (err) {
        setError('An error occurred while fetching hotel details.');
        console.error('Error fetching hotel details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    fetchInsights();
  }, [hotel_id, fetchInsights]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleApplyFilters = () => {
    fetchInsights();
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      roomType: '',
      acType: '',
      priceRangeMin: '',
      priceRangeMax: '',
      menuCategory: '',
      limit: 10
    });
    // Automatically fetch data with cleared filters
    setTimeout(() => {
      fetchInsights();
    }, 100);
  };

  const renderStarRating = (rating) => {
    return [...Array(rating)].map((_, index) => (
      <i key={index} className="bi bi-star-fill text-warning"></i>
    ));
  };

  const renderInsightsTab = () => {
    if (!insightsData) return <Alert variant="info">No insights data available</Alert>;

    return (
      <>
        {/* Filter Section */}
        <Card className="mb-4">
          <Card.Body>
            <Card.Title as="h5" className="mb-4">
              Filters
            </Card.Title>
            <Row className="g-3">
              {/* Date Range Filters */}
              <Col xs={12} md={6} lg={3}>
                <Form.Group>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6} lg={3}>
                <Form.Group>
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </Form.Group>
              </Col>

              {/* Room Filters */}
              <Col xs={12} md={6} lg={3}>
                <Form.Group>
                  <Form.Label>Room Type</Form.Label>
                  <Form.Select
                    value={filters.roomType}
                    onChange={(e) => handleFilterChange('roomType', e.target.value)}
                  >
                    <option value="">All Room Types</option>
                    <option value="SINGLE">Single</option>
                    <option value="DOUBLE">Double</option>
                    <option value="SUITE">Suite</option>
                    <option value="DORMETORY">Dormitory</option>
                    <option value="BANQUET HALL">Banquet Hall</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12} md={6} lg={3}>
                <Form.Group>
                  <Form.Label>AC Type</Form.Label>
                  <Form.Select
                    value={filters.acType}
                    onChange={(e) => handleFilterChange('acType', e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="AC">AC</option>
                    <option value="NON-AC">Non-AC</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Price Range Filters */}
              <Col xs={12} md={6} lg={3}>
                <Form.Group>
                  <Form.Label>Min Price (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Min price"
                    value={filters.priceRangeMin}
                    onChange={(e) => handleFilterChange('priceRangeMin', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6} lg={3}>
                <Form.Group>
                  <Form.Label>Max Price (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Max price"
                    value={filters.priceRangeMax}
                    onChange={(e) => handleFilterChange('priceRangeMax', e.target.value)}
                  />
                </Form.Group>
              </Col>

              {/* Menu Category Filter */}
              <Col xs={12} md={6} lg={3}>
                <Form.Group>
                  <Form.Label>Menu Category</Form.Label>
                  <Form.Select
                    value={filters.menuCategory}
                    onChange={(e) => handleFilterChange('menuCategory', e.target.value)}
                  >
                    <option value="">All Categories</option>
                    <option value="Starter">Starter</option>
                    <option value="Main Course">Main Course</option>
                    <option value="Dessert">Dessert</option>
                    <option value="Beverage">Beverage</option>
                    <option value="Breakfast">Breakfast</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Results Limit */}
              <Col xs={12} md={6} lg={3}>
                <Form.Group>
                  <Form.Label>Results Limit</Form.Label>
                  <Form.Select
                    value={filters.limit}
                    onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                  >
                    <option value={5}>Top 5</option>
                    <option value={10}>Top 10</option>
                    <option value={20}>Top 20</option>
                    <option value={50}>Top 50</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Filter Action Buttons */}
            <div className="d-flex gap-2 mt-3">
              <Button 
                variant="success" 
                onClick={handleApplyFilters}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Apply Filters'}
              </Button>
              <Button 
                variant="outline-secondary" 
                onClick={handleClearFilters}
                disabled={loading}
              >
                Clear Filters
              </Button>
            </div>
          </Card.Body>
        </Card>

        <Row>
        <Col md={12} className="mb-4">
          <Card>
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">Top Performing Rooms</h5>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Sr. No</th>
                    <th>Room Number</th>
                    <th>Type</th>
                    <th>AC/Non-AC</th>
                    <th>Tariff</th>
                    <th>Total Bookings</th>
                  </tr>
                </thead>
                <tbody>
                  {insightsData.topRooms.map((room,i) => (
                    <tr key={room.room_number}>
                      <td>{i+1}</td>
                      <td>{room.room_number}</td>
                      <td>{room.room_type}</td>
                      <td>{room.ac_no_ac}</td>
                      <td>₹{room.tariff}</td>
                      <td>
                        <Badge bg="success">{room.booking_count}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="mb-4">
          <Card>
            <Card.Header className="bg-success text-white">
              <h5 className="mb-0">Popular Menu Items</h5>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                  <th>Sr. No</th>
                    <th>Item Name</th>
                    <th>Category</th>
                    <th>Orders</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {insightsData.topMenuItems.length > 0 ? (
                    insightsData.topMenuItems.map((item,i) => (
                      <tr key={item.name}>
                        <td>{i+1}</td>
                        <td>{item.name}</td>
                        <td>{item.category}</td>
                        <td>{item.order_count}</td>
                        <td>{item.total_quantity}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">No menu data available</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="mb-4">
          <Card>
            <Card.Header className="bg-info text-white">
              <h5 className="mb-0">Spa Appointments</h5>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Sr. No</th>
                    <th>Appointment ID</th>
                    <th>Guest Name</th>
                    <th>Mobile Number</th>
                    <th>Service</th>
                    <th>Price</th>
                    <th>Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {spaAppointments.length > 0 ? (
                    spaAppointments.map((appointment,i) => (
                      <tr key={appointment.id}>
                        <td>{i+1}</td>
                        <td>{appointment.id}</td>
                        <td>{appointment.name}</td>
                        <td>{appointment.mobile_number}</td>
                        <td>{appointment.services.map(service => service.service_name).join(', ')}</td>
                        <td>₹{appointment.services.reduce((total, service) => total + parseFloat(service.price), 0).toFixed(2)}</td>
                        <td>{format(new Date(appointment.appointment_time), 'PPp')}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">No spa appointments available</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
        </Row>
      </>
    );
  };

  const downloadExcel = () => {
    try {
      if (!insightsData) {
        console.log('No insights data available');
        return;
      }

      // Create a new workbook
      const wb = XLSX.utils.book_new();

      // Process Top Rooms
      const roomsData = insightsData.topRooms.map(room => ({
        "Room Number": room.room_number,
        "Room Type": room.room_type,
        "AC/Non-AC": room.ac_no_ac,
        "Tariff": room.tariff,
        "Total Bookings": room.booking_count
      }));

      // Process Menu Items
      const menuData = insightsData.topMenuItems.map(item => ({
        "Item Name": item.name,
        "Category": item.category,
        "Price": item.price,
        "Orders": item.order_count,
        "Total Quantity": item.total_quantity
      }));

      // Process Spa Services
      const spaData = insightsData.topSpaServices.map(service => ({
        "Service Name": service.name,
        "Duration (mins)": service.duration,
        "Price": service.price,
        "Total Appointments": service.appointment_count
      }));

      // Add worksheets to workbook
      const wsRooms = XLSX.utils.json_to_sheet(roomsData);
      const wsMenu = XLSX.utils.json_to_sheet(menuData);
      const wsSpa = XLSX.utils.json_to_sheet(spaData);

      // Add the sheets to the workbook
      XLSX.utils.book_append_sheet(wb, wsRooms, "Top Rooms");
      XLSX.utils.book_append_sheet(wb, wsMenu, "Menu Items");
      XLSX.utils.book_append_sheet(wb, wsSpa, "Spa Services");

      // Auto-size columns for all sheets
      [wsRooms, wsMenu, wsSpa].forEach(ws => {
        const range = XLSX.utils.decode_range(ws['!ref']);
        const colWidths = [];
        for (let C = range.s.c; C <= range.e.c; ++C) {
          let maxWidth = 10;
          for (let R = range.s.r; R <= range.e.r; ++R) {
            const cell = ws[XLSX.utils.encode_cell({r: R, c: C})];
            if (cell && cell.v) {
              const width = (cell.v.toString().length + 2) * 1.2;
              maxWidth = Math.max(maxWidth, width);
            }
          }
          colWidths[C] = { wch: maxWidth };
        }
        ws['!cols'] = colWidths;
      });

      // Save the workbook
      XLSX.writeFile(wb, 'Hotel_Insights_Report.xlsx');
      console.log('Excel file saved with data:', {
        rooms: roomsData,
        menu: menuData,
        spa: spaData
      });
    } catch (error) {
      console.error('Error generating Excel:', error);
      console.log('Insights Data:', insightsData);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  const data = hotelData || {};

  return (
    <Container className="px-3 mt-5 py-5">
      <div className="d-flex justify-content-end mb-3">
        <Button color="secondary" onClick={() => navigate("/hotels")} className="w-100 w-md-auto">
          Back
        </Button>
      </div>
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={8}>
              <h2>{data.hotel_name} <Badge bg={data.status === 'Approved' ? 'success' : 'warning'}>{data.status}</Badge></h2>
              <div>{renderStarRating(data.star_rating)} <span className="ms-2">({data.star_rating} Stars)</span></div>
              <p><i className="bi bi-geo-alt"></i> {data.address}</p>
              <p>Total Rooms: {data.total_rooms} | Type: {data.type}</p>
              {data.days_remaining !== null && data.days_remaining !== undefined && (
                <div className="mt-2">
                  <Badge 
                    bg={data.days_remaining > 30 ? 'success' : data.days_remaining > 7 ? 'warning' : 'danger'}
                    className="fs-6"
                  >
                    <i className="bi bi-clock me-1"></i>
                    {data.days_remaining > 0 ? `${data.days_remaining} days remaining` : 'Subscription expired'}
                  </Badge>
                  {data.expires_on && (
                    <small className="text-muted ms-2">
                      Expires: {format(new Date(data.expires_on), 'MMM dd, yyyy')}
                    </small>
                  )}
                </div>
              )}
            </Col>
            <Col md={4}>
              <Card>
                <Card.Body>
                  <h5>Owner Details</h5>
                  <p><i className="bi bi-person"></i> {data.owner?.name}</p>
                  <p><i className="bi bi-envelope"></i> {data.owner?.email}</p>
                  <p><i className="bi bi-telephone"></i> {data.owner?.mobile}</p>
                </Card.Body>
              </Card>
              
              {data.days_remaining !== null && data.days_remaining !== undefined && (
                <Card className="mt-3">
                  <Card.Body>
                    <h5>Subscription Status</h5>
                    <div className="text-center">
                      <Badge 
                        bg={data.days_remaining > 30 ? 'success' : data.days_remaining > 7 ? 'warning' : 'danger'}
                        className="fs-5 px-3 py-2"
                      >
                        <i className="bi bi-clock me-2"></i>
                        {data.days_remaining > 0 ? `${data.days_remaining} days remaining` : 'Subscription expired'}
                      </Badge>
                      {data.expires_on && (
                        <div className="mt-2">
                          <small className="text-muted">
                            <i className="bi bi-calendar-event me-1"></i>
                            Expires: {format(new Date(data.expires_on), 'MMM dd, yyyy')}
                          </small>
                        </div>
                      )}
                      {data.days_remaining <= 7 && data.days_remaining > 0 && (
                        <div className="mt-2">
                          <small className="text-warning">
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            Subscription expires soon!
                          </small>
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        <Tab eventKey="rooms" title="Rooms">
          <Table striped bordered responsive>
            <thead>
              <tr>
                <th>Sr. No.</th>
                <th>Room Number</th>
                <th>Type</th>
                <th>AC/Non-AC</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.Rooms?.map((room,i) => (
                <tr key={room.room_number}>
                  <td>{i+1}</td>
                  <td style={{cursor:'pointer'}} className="text-success cursor-pointer" onClick={()=>{  navigate(`/room-history/${room.room_id}/${room.room_number}`)}}>{room.room_number}</td>
                  <td>{room.room_type}</td>
                  <td>{room.ac_no_ac}</td>
                  <td><Badge bg={room.roomstatus === 'AVAILABEL' ? 'success' : 'danger'}>{room.roomstatus}</Badge></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Tab>

        <Tab eventKey="employees" title="Employees">
          <Table striped bordered responsive>
            <thead>
              <tr>
                <th>Sr. No</th>
                {/* <th>Employee ID</th> */}
                <th>Name</th>
                <th>Role</th>
                <th>BasicSalary</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {data.employees?.map((employee,i) => (
                <tr key={employee.EmployeeID}>
                  <td>{i+1}</td>
                  {/* <td>{employee.EmployeeID}</td> */}
                  <td>{employee.EmployeeName}</td>
                  <td>{employee.role}</td>
                  <td>{employee.BasicSalary}</td>
                  <td>{employee.email}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Tab>

        <Tab eventKey="spa" title="Spa Appointments">
          <Table striped bordered responsive>
            <thead>
              <tr>
                <th>Sr. No</th>
                {/* <th>Appointment ID</th> */}
                <th>Guest Name</th>
                <th>Therapist</th>
                <th>Service</th>
                <th>Date & Time</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.appointments?.map((appointment,i) => (
                <tr key={appointment.id}>
                  <td>{ i+ 1}</td>
                  {/* <td>{appointment.id}</td> */}
                  <td>{appointment.name}</td>
                  <td>{appointment.therapist_name}</td>
                  <td>{appointment.SpaService?.service_name}</td>
                  <td>{format(new Date(appointment.appointment_time), 'PPp')}</td>
                  <td><SpaAppointmentStatusBadge status={appointment.status} /></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Tab>

        <Tab eventKey="insights" title="Insights">
        <Col md={3} className="my-2">
                 <Button 
                    variant="success" 
                    className="mt-3 w-100"
                    onClick={downloadExcel}
                  >
                    <i className="bi bi-download me-2"></i>
                    Download Report
                  </Button>
                  </Col>
          {renderInsightsTab()}
        </Tab>
      </Tabs>
    </Container>
  );
};

export default HotelDetails;
